import { useEffect, useRef, useState } from 'react';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { PageBreadcrumbs } from '@/ui/layout/PageBreadcrumbs';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { MenuIcon, NotificationsIcon, PersonIcon } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import {
  Box,
  Drawer,
  IconButton,
  ListSubheader,
  MenuItem,
  MenuList,
  Text,
  useTheme,
} from '../primitives';
import { Popover } from '../primitives/Popover';
import { Stack } from './Stack';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';

type AppShellProps = PropsWithChildren<{
  title?: ReactNode;
  nav?: ReactNode;
  actions?: ReactNode;
  sidebar?: ReactNode;
  showDefaultActions?: boolean;
}>;

export const AppShell = ({
  title,
  nav,
  actions,
  sidebar,
  showDefaultActions = true,
  children,
}: AppShellProps): ReactElement => {
  const theme = useTheme();
  const layoutMode = useLayoutMode();
  const { items: breadcrumbItems, visible: breadcrumbsVisible } = useBreadcrumbs();
  const showBreadcrumbs = breadcrumbsVisible && breadcrumbItems.length > 0;
  const isMobile = layoutMode !== layoutModeTokens.wide;
  const headerPaddingX = isMobile ? s('sm') : s('md');
  const [open, setOpen] = useState(false);
  const [notificationsAnchor, setNotificationsAnchor] = useState<HTMLElement | null>(null);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const hasSidebar = Boolean(sidebar);
  const useCurvedDesktopMainChrome = hasSidebar && !isMobile;
  const hasHeader = [title, nav, actions, hasSidebar].some(Boolean);
  const useGridHeaderLayout = isMobile && hasSidebar;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const offset = hasSidebar && !isMobile ? `${theme.app.sizes.sidebarWidth}px` : '0px';
    document.body.style.setProperty('--app-dialog-offset', offset);
    return () => {
      document.body.style.removeProperty('--app-dialog-offset');
    };
  }, [hasSidebar, isMobile, theme.app.sizes.sidebarWidth]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const headerElement = headerRef.current;
    if (!headerElement) {
      document.body.style.setProperty('--app-topbar-height', '0px');
      return () => {
        document.body.style.removeProperty('--app-topbar-height');
      };
    }

    const updateTopbarHeight = (): void => {
      const nextHeight = Math.round(headerElement.getBoundingClientRect().height);
      document.body.style.setProperty('--app-topbar-height', `${nextHeight}px`);
    };

    const frameId = window.requestAnimationFrame(updateTopbarHeight);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            updateTopbarHeight();
          });
    resizeObserver?.observe(headerElement);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      document.body.style.removeProperty('--app-topbar-height');
    };
  }, [hasHeader, headerPaddingX]);
  return (
    <Box
      sx={(currentTheme) => ({
        '--app-shell-corner-radius': '20px',
        height: '100dvh',
        minHeight: '100vh',
        backgroundColor: `${currentTheme.app.colors.bg} !important`,
        backgroundImage: 'none',
        opacity: 1,
        isolation: 'isolate',
        display: 'grid',
        gridTemplateRows: hasHeader ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)',
        overflow: 'hidden',
      })}
    >
      <a className="flow-skip-link" href="#main-content">
        Skip to main content
      </a>
      {hasHeader && (
        <Box
          component="header"
          ref={headerRef}
          sx={(currentTheme) => ({
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: `${currentTheme.app.colors.surfaceAlt} !important`,
            backgroundImage: 'none',
            opacity: 1,
            isolation: 'isolate',
            borderBottom: useCurvedDesktopMainChrome
              ? 'none'
              : `1px solid ${currentTheme.app.colors.border}`,
            paddingTop: s('none'),
            paddingBottom: useCurvedDesktopMainChrome ? s('none') : s('xs'),
            minWidth: 0,
          })}
        >
          <Box
            sx={{
              display: useGridHeaderLayout ? 'grid' : 'flex',
              gridTemplateColumns: useGridHeaderLayout
                ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
                : undefined,
              alignItems: 'center',
              gap: s('md'),
              flexWrap: 'nowrap',
              minWidth: 0,
              width: '100%',
              px: headerPaddingX,
            }}
          >
            {hasSidebar && isMobile ? (
              <IconButton
                aria-label="Open navigation"
                onClick={() => {
                  setOpen(true);
                }}
                sx={{
                  '&:hover': { backgroundColor: 'transparent' },
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: s('sm'),
                  marginTop: (muiTheme) => muiTheme.spacing(0),
                  padding: (muiTheme) => muiTheme.spacing(0.25),
                  width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  lineHeight: 0,
                  '& .MuiBox-root': {
                    width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  },
                  '& .MuiBox-root svg': {
                    width: '100%',
                    height: '100%',
                  },
                  '& svg': {
                    width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    display: 'block',
                  },
                }}
              >
                <MenuIcon fontSize="inherit" />
              </IconButton>
            ) : null}
            {title ? (
              <Box
                sx={{
                  justifySelf: useGridHeaderLayout ? 'center' : 'start',
                  textAlign: useGridHeaderLayout ? 'center' : 'left',
                }}
              >
                <Text role="sectionTitle">{title}</Text>
              </Box>
            ) : null}
            {nav && !useGridHeaderLayout ? (
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>{nav}</Box>
            ) : !useGridHeaderLayout ? (
              <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            ) : null}
            <Stack
              direction="row"
              gap="sm"
              sx={{
                justifySelf: useGridHeaderLayout ? 'stretch' : 'end',
                justifyContent: useGridHeaderLayout ? 'flex-end' : undefined,
                minWidth: 0,
              }}
            >
              {showDefaultActions ? (
                <>
                  <IconButton
                    size="large"
                    aria-label="Notifications"
                    onClick={(event) => {
                      setNotificationsAnchor(event.currentTarget);
                    }}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '& svg': {
                        width: (muiTheme) => muiTheme.app.iconSizes.lg,
                        height: (muiTheme) => muiTheme.app.iconSizes.lg,
                      },
                    }}
                  >
                    <NotificationsIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="large"
                    aria-label="Account"
                    onClick={(event) => {
                      setUserAnchor(event.currentTarget);
                    }}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '& svg': {
                        width: (muiTheme) => muiTheme.app.iconSizes.lg,
                        height: (muiTheme) => muiTheme.app.iconSizes.lg,
                      },
                    }}
                  >
                    <PersonIcon fontSize="inherit" />
                  </IconButton>
                </>
              ) : null}
              {actions ? <Box>{actions}</Box> : null}
            </Stack>
          </Box>
        </Box>
      )}
      <Box
        sx={(currentTheme) => ({
          display: 'grid',
          gridTemplateColumns: hasSidebar ? (isMobile ? '1fr' : 'auto 1fr') : '1fr',
          minHeight: 0,
          backgroundColor: useCurvedDesktopMainChrome
            ? `${currentTheme.app.colors.surfaceAlt} !important`
            : `${currentTheme.app.colors.bg} !important`,
          backgroundImage: 'none',
          opacity: 1,
          isolation: 'isolate',
          overflow: 'hidden',
        })}
      >
        {hasSidebar ? (
          <>
            <Box
              component="aside"
              sx={(currentTheme) => {
                const sidebarOffset = 'var(--app-topbar-height, 54px)';
                return {
                  display: isMobile ? 'none' : 'block',
                  width: currentTheme.app.sizes.sidebarWidth,
                  backgroundColor: `${currentTheme.app.colors.surfaceAlt} !important`,
                  backgroundImage: 'none',
                  opacity: 1,
                  isolation: 'isolate',
                  borderRight: useCurvedDesktopMainChrome
                    ? 'none'
                    : `1px solid ${currentTheme.app.colors.border}`,
                  paddingX: s('md'),
                  paddingY: s('lg'),
                  position: 'sticky',
                  top: sidebarOffset,
                  alignSelf: 'start',
                  height: `calc(100vh - ${sidebarOffset})`,
                  overflow: 'auto',
                };
              }}
            >
              {sidebar}
            </Box>
            <Drawer
              open={open}
              onClose={() => {
                setOpen(false);
              }}
              sx={{ display: isMobile ? 'block' : 'none' }}
              slotProps={{
                paper: {
                  sx: {
                    width: theme.app.sizes.sidebarWidth,
                    paddingX: s('md'),
                    paddingY: s('lg'),
                    backgroundColor: `${theme.app.colors.surfaceAlt} !important`,
                    borderRight: `1px solid ${theme.app.colors.border}`,
                    backgroundImage: 'none',
                    opacity: 1,
                    isolation: 'isolate',
                    height: '100%',
                  },
                },
              }}
            >
              <Box
                sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
                onClickCapture={(event) => {
                  const target = event.target as HTMLElement | null;
                  if (!target) return;
                  if (target.closest('a[data-has-popover="true"]')) return;
                  if (target.closest('a[href]')) {
                    setOpen(false);
                  }
                }}
              >
                {sidebar}
              </Box>
            </Drawer>
          </>
        ) : null}
        <Box
          component="main"
          id="main-content"
          sx={(currentTheme) => ({
            flex: 1,
            minWidth: 0,
            px: isMobile && hasSidebar ? s('sm') : s('none'),
            minHeight: 0,
            overflowY: 'auto',
            scrollbarGutter: isMobile ? 'auto' : 'stable both-edges',
            overscrollBehavior: 'auto',
            backgroundColor: `${currentTheme.app.colors.bg} !important`,
            backgroundImage: 'none',
            opacity: 1,
            isolation: 'isolate',
            scrollbarColor: `${currentTheme.app.colors.border} ${currentTheme.app.colors.bg}`,
            '&::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
              backgroundColor: currentTheme.app.colors.bg,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: currentTheme.app.colors.bg,
            },
            '&::-webkit-scrollbar-corner': {
              backgroundColor: currentTheme.app.colors.bg,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: currentTheme.app.colors.border,
              borderRadius: '999px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: currentTheme.palette.text.secondary,
            },
            borderTopLeftRadius: useCurvedDesktopMainChrome
              ? 'var(--app-shell-corner-radius)'
              : undefined,
            borderTop: useCurvedDesktopMainChrome
              ? `1px solid ${currentTheme.app.colors.border}`
              : 'none',
            borderLeft: useCurvedDesktopMainChrome
              ? `1px solid ${currentTheme.app.colors.border}`
              : 'none',
            boxSizing: 'border-box',
            overflowX: useCurvedDesktopMainChrome ? 'hidden' : 'auto',
          })}
        >
          {showBreadcrumbs ? (
            <Box
              sx={(currentTheme) => ({
                borderBottom: `1px solid ${currentTheme.app.colors.border}`,
                paddingX: s('md'),
                paddingTop: s('md'),
                paddingBottom: s('md'),
              })}
            >
              <PageBreadcrumbs />
            </Box>
          ) : null}
          {children}
        </Box>
      </Box>
      {showDefaultActions ? (
        <>
          <Popover
            open={Boolean(notificationsAnchor)}
            anchorEl={notificationsAnchor}
            onClose={() => {
              setNotificationsAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth={240}
          >
            <MenuList
              dense
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    typography: 'label',
                    paddingX: s('sm'),
                    paddingY: s('xs'),
                    lineHeight: 1.4,
                    backgroundColor: 'transparent',
                  }}
                >
                  Notifications
                </ListSubheader>
              }
            >
              <MenuItem
                sx={{
                  minHeight: s('2xl'),
                  paddingY: s('xs'),
                  typography: 'body',
                  fontSize: (muiTheme) => muiTheme.typography.caption.fontSize,
                  lineHeight: (muiTheme) => muiTheme.typography.caption.lineHeight,
                  fontWeight: (muiTheme) => muiTheme.typography.caption.fontWeight,
                  color: 'text.secondary',
                }}
              >
                No new alerts.
              </MenuItem>
            </MenuList>
          </Popover>
          <Popover
            open={Boolean(userAnchor)}
            anchorEl={userAnchor}
            onClose={() => {
              setUserAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth={200}
          >
            <MenuList
              dense
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    typography: 'label',
                    paddingX: s('sm'),
                    paddingY: s('xs'),
                    lineHeight: 1.4,
                    backgroundColor: 'transparent',
                  }}
                >
                  Account
                </ListSubheader>
              }
            >
              <MenuItem
                sx={{
                  minHeight: s('2xl'),
                  paddingY: s('xs'),
                  typography: 'body',
                  fontSize: (muiTheme) => muiTheme.typography.caption.fontSize,
                  lineHeight: (muiTheme) => muiTheme.typography.caption.lineHeight,
                  fontWeight: (muiTheme) => muiTheme.typography.caption.fontWeight,
                  color: 'text.secondary',
                }}
              >
                Signed in as demo@flowqa.dev
              </MenuItem>
            </MenuList>
          </Popover>
        </>
      ) : null}
    </Box>
  );
};
